import React, { useEffect, useState, useMemo } from "react";
import { api, fmtMoney, fmtPct, fmtDate } from "../lib/api";
import { AXIS_STROKE, AXIS_LINE, GRID_STROKE, TOOLTIP_CONTENT_STYLE, TOOLTIP_ITEM_STYLE, TOOLTIP_LABEL_STYLE, dollarTick } from "../lib/chartStyles";
import { TrendingUp, TrendingDown, DollarSign, Calendar, Activity, Plus, Layers, Check, PieChart as PieIcon, AlertTriangle, AlertCircle } from "lucide-react";
import { Link } from "react-router-dom";
import HoldingDialog from "../components/HoldingDialog";
import AlertsBanner from "../components/AlertsBanner";
import ConfirmMarkPaidDialog from "../components/ConfirmMarkPaidDialog";
import { toast } from "sonner";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";

// Type → label + colour. Kept in sync with Holdings.jsx TYPE_CHIP_CLS
// (same hex codes from tailwind palette) so the visual identity is consistent.
const TYPE_LABEL = {
  babybond: "Babybonds", preferred: "Preferred", bdc: "BDCs", reit: "REITs",
  etf: "ETFs", common: "Common", other: "Other", pending: "Pending",
};
const TYPE_COLOR = {
  babybond:  "#9e8193", // paars-dark
  preferred: "#c9b896", // gold
  bdc:       "#88a17e", // groen
  reit:      "#bfaf9d", // stone-warm
  etf:       "#d7b5c9", // paars
  common:    "#dbd6d2", // stone
  other:     "#a86464", // burgundy
  pending:   "#9e8193", // fallback paars-dark
};

const Stat = ({ label, value, sub, accent, testid }) => (
  <div className="bg-champagne border border-stone p-5 flex flex-col justify-between min-h-[124px]" data-testid={testid}>
    <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-graphite/55">{label}</div>
    <div>
      <div className={`text-3xl font-mono tracking-tight ${accent || "text-graphite"}`}>{value}</div>
      {sub && <div className="text-xs font-mono text-graphite/55 mt-1">{sub}</div>}
    </div>
  </div>
);

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openAdd, setOpenAdd] = useState(false);
  const [confirmPay, setConfirmPay] = useState(null);
  const [busyPay, setBusyPay] = useState(false);
  const [riskEvents, setRiskEvents] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: sum }, riskRes] = await Promise.all([
        api.get("/portfolio/summary"),
        // Don't break Dashboard if risk endpoint hiccups
        api.get("/risk-events?limit=8").catch(() => ({ data: [] })),
      ]);
      setSummary(sum);
      setRiskEvents(riskRes.data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const onMarkPaid = async () => {
    if (!confirmPay) return;
    setBusyPay(true);
    try {
      await api.post("/dividends/mark-paid", {
        holding_id: confirmPay.holding_id,
        payment_date: confirmPay.date,
        amount: confirmPay.amount,
      });
      toast.success(`Recorded ${confirmPay.ticker} payment`);
      setConfirmPay(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusyPay(false);
    }
  };

  // Build chart data: monthly forecast bars
  const chartData = (() => {
    if (!summary?.upcoming_payments) return [];
    const buckets = {};
    summary.upcoming_payments.forEach((p) => {
      const k = p.date.slice(0, 7);
      buckets[k] = (buckets[k] || 0) + p.amount;
    });
    return Object.entries(buckets).slice(0, 12).map(([k, v]) => ({
      month: k,
      amount: Number(v.toFixed(2)),
    }));
  })();

  // Allocation by asset_type — recharts wants [{name, value, key}]
  const allocationData = useMemo(() => {
    const raw = summary?.allocation_by_type || [];
    return raw
      .filter((r) => r.market_value > 0)
      .map((r) => ({
        key: r.asset_type,
        name: TYPE_LABEL[r.asset_type] || r.asset_type,
        value: r.market_value,
        positions: r.positions,
        annual_income: r.annual_income,
        pct: r.pct_market,
      }));
  }, [summary]);

  const plPositive = (summary?.unrealized_pl ?? 0) >= 0;

  return (
    <div className="min-h-screen bg-champagne">
      <div className="border-b border-stone px-8 py-5 flex items-center justify-between bg-champagne sticky top-0 z-10">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-paars-dark font-bold">// SYS::DASHBOARD</div>
          <h1 className="text-2xl font-bold tracking-tight text-graphite">Portfolio Overview</h1>
        </div>
        <button data-testid="dashboard-add-btn" onClick={()=>setOpenAdd(true)} className="flex items-center gap-2 bg-paars hover:bg-paars-light text-graphite px-4 py-2.5 font-semibold text-sm">
          <Plus className="w-4 h-4" strokeWidth={2.5} />
          ADD POSITION
        </button>
      </div>

      <div className="p-8 space-y-8">
        {/* Cut-Risk banner — rendered above all charts so it grabs attention.
            Only shows when there's at least one HIGH or MEDIUM event in the
            past 14 days. INFO-only events are filtered server-side via prefs. */}
        {riskEvents.filter((e) => ["high", "medium"].includes(e.severity)).length > 0 && (
          <RiskEventsBanner events={riskEvents} />
        )}
        {loading ? (
          <div className="text-graphite/55 font-mono text-sm animate-pulse">Loading portfolio...</div>
        ) : (
          <>
            {/* Top stats row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-stone border border-stone">
              <Stat
                testid="stat-portfolio-value"
                label="Portfolio Value"
                value={fmtMoney(summary.total_market_value)}
                sub={`Cost basis: ${fmtMoney(summary.total_cost_basis)}`}
              />
              <Stat
                testid="stat-pl"
                label="Unrealized P/L"
                value={`${plPositive ? "+" : ""}${fmtMoney(summary.unrealized_pl)}`}
                sub={`${plPositive ? "+" : ""}${fmtPct(summary.unrealized_pl_pct)}`}
                accent={plPositive ? "text-groen-dark" : "text-burgundy"}
              />
              <Stat
                testid="stat-yoc"
                label="Avg Yield on Cost"
                value={fmtPct(summary.weighted_yoc, 3)}
                sub="Weighted by cost basis"
                accent="text-paars-dark"
              />
              <Stat
                testid="stat-ytm"
                label="Avg Yield to Maturity"
                value={fmtPct(summary.weighted_ytm, 3)}
                sub="Weighted by market value"
                accent="text-paars-dark"
              />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-stone border border-stone">
              <Stat
                label="Annual Income (Proj)"
                value={fmtMoney(summary.projected_annual_income)}
                sub="From all active positions"
                accent="text-groen-dark"
              />
              <Stat
                label="Dividends YTD"
                value={fmtMoney(summary.ytd_dividends_received)}
                sub={`Lifetime: ${fmtMoney(summary.total_dividends_received)}`}
              />
              <Stat
                label="Holdings"
                value={summary.holdings_count}
                sub="Active positions"
              />
              <Stat
                label="Next Payment"
                value={summary.upcoming_payments?.[0] ? fmtMoney(summary.upcoming_payments[0].amount) : "—"}
                sub={summary.upcoming_payments?.[0] ? `${summary.upcoming_payments[0].ticker} · ${fmtDate(summary.upcoming_payments[0].date)}` : "No upcoming"}
                accent="text-paars-dark"
              />
            </div>

            {/* Chart + Upcoming */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-stone border border-stone">
              <div className="bg-champagne p-6 lg:col-span-2">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <div className="text-[10px] tracking-[0.25em] uppercase text-graphite/55 font-bold">// FORECAST</div>
                    <h3 className="text-base font-semibold text-graphite mt-1">Projected Dividend Income (12mo)</h3>
                  </div>
                  <Activity className="w-4 h-4 text-paars-dark" />
                </div>
                <div className="h-72">
                  {chartData.length === 0 ? (
                    <div className="h-full flex items-center justify-center text-graphite/45 text-sm font-mono">
                      Add positions to forecast income
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData}>
                        <CartesianGrid stroke={GRID_STROKE} strokeDasharray="0" vertical={false} />
                        <XAxis dataKey="month" stroke={AXIS_STROKE} fontSize={11} tickLine={false} axisLine={AXIS_LINE} />
                        <YAxis stroke={AXIS_STROKE} fontSize={11} tickLine={false} axisLine={AXIS_LINE} tickFormatter={dollarTick} />
                        <Tooltip
                          contentStyle={TOOLTIP_CONTENT_STYLE}
                          itemStyle={TOOLTIP_ITEM_STYLE}
                          labelStyle={TOOLTIP_LABEL_STYLE}
                          formatter={(v) => [`$${v}`, "Income"]}
                        />
                        <Bar dataKey="amount" fill="#9e8193" />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>

              <div className="bg-champagne p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <div className="text-[10px] tracking-[0.25em] uppercase text-graphite/55 font-bold">// UPCOMING</div>
                    <h3 className="text-base font-semibold text-graphite mt-1">Next Payments</h3>
                  </div>
                  <Calendar className="w-4 h-4 text-paars-dark" />
                </div>
                <div className="space-y-px bg-stone" data-testid="upcoming-list">
                  {(summary.upcoming_payments?.slice(0, 8) || []).map((p) => (
                    <div key={`${p.holding_id || p.ticker}-${p.date}`} className="bg-champagne p-3 flex items-center justify-between group">
                      <div>
                        <div className="text-sm font-mono text-graphite">{p.ticker}</div>
                        <div className="text-[10px] font-mono text-graphite/55 uppercase tracking-widest">{fmtDate(p.date)}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-sm font-mono text-groen-dark">{fmtMoney(p.amount)}</div>
                        <button
                          data-testid={`mark-paid-${p.holding_id || p.ticker}-${p.date}`}
                          onClick={() => setConfirmPay(p)}
                          title="Mark as paid"
                          className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 text-groen-dark hover:bg-stone/50 border border-groen/30 hover:border-groen"
                        >
                          <Check className="w-3.5 h-3.5" strokeWidth={2.5} />
                        </button>
                      </div>
                    </div>
                  ))}
                  {(!summary.upcoming_payments || summary.upcoming_payments.length === 0) && (
                    <div className="bg-champagne text-graphite/45 text-xs font-mono p-4">No upcoming payments</div>
                  )}
                </div>
              </div>
            </div>

            <div className="border border-stone p-6 bg-champagne flex items-center justify-between">
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-graphite/55 font-bold">// QUICK_ACCESS</div>
                <h3 className="text-base font-semibold text-graphite mt-1">Manage your portfolio</h3>
              </div>
              <div className="flex gap-2">
                <Link to="/holdings" className="px-4 py-2.5 border border-paars-dark/30 text-graphite/80 text-sm hover:bg-stone/50 flex items-center gap-2">
                  <Layers className="w-4 h-4" /> View Holdings
                </Link>
                <Link to="/dividends" className="px-4 py-2.5 border border-paars-dark/30 text-graphite/80 text-sm hover:bg-stone/50 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" /> Dividends
                </Link>
              </div>
            </div>

            {/* ───────── Portfolio Allocation (by asset_type) ───────── */}
            {allocationData.length > 0 && (
              <div data-testid="allocation-card" className="border border-stone bg-champagne p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <div className="text-[10px] tracking-[0.25em] uppercase text-graphite/55 font-bold">// ALLOCATION</div>
                    <h3 className="text-base font-semibold text-graphite mt-1">Portfolio by Asset Type</h3>
                  </div>
                  <PieIcon className="w-4 h-4 text-paars-dark" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                  <div className="h-64" data-testid="allocation-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={allocationData}
                          dataKey="value"
                          nameKey="name"
                          innerRadius={55}
                          outerRadius={95}
                          paddingAngle={1}
                          stroke="#f2f2f2"
                          strokeWidth={2}
                        >
                          {allocationData.map((entry) => (
                            <Cell key={entry.key} fill={TYPE_COLOR[entry.key] || "#9e8193"} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={TOOLTIP_CONTENT_STYLE}
                          formatter={(v, n, props) => [`${fmtMoney(v)} · ${props.payload.positions} pos`, props.payload.name]}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="space-y-px bg-stone" data-testid="allocation-legend">
                    {allocationData.map((slice) => (
                      <Link
                        key={slice.key}
                        to={`/holdings?type=${slice.key}`}
                        data-testid={`allocation-row-${slice.key}`}
                        className="bg-champagne flex items-center justify-between p-3 hover:bg-white transition-colors group"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="w-3 h-3 flex-shrink-0" style={{ background: TYPE_COLOR[slice.key] || "#9e8193" }} />
                          <div className="min-w-0">
                            <div className="text-sm font-mono text-graphite group-hover:text-paars-dark truncate">{slice.name}</div>
                            <div className="text-[10px] font-mono text-graphite/55">{slice.positions} pos · {fmtMoney(slice.annual_income)}/yr</div>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="text-sm font-mono text-graphite">{fmtMoney(slice.value)}</div>
                          <div className="text-[10px] font-mono text-paars-dark">{fmtPct(slice.pct)}</div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <HoldingDialog open={openAdd} onOpenChange={setOpenAdd} editing={null} onSaved={load} />
      <ConfirmMarkPaidDialog
        open={!!confirmPay}
        onOpenChange={(o) => !o && setConfirmPay(null)}
        payment={confirmPay}
        onConfirm={onMarkPaid}
        busy={busyPay}
      />
    </div>
  );
}


const SEV_META = {
  high:   { Icon: AlertCircle,    cls: "bg-burgundy text-champagne",        border: "border-burgundy",   text: "text-burgundy" },
  medium: { Icon: AlertTriangle,  cls: "bg-gold text-graphite",             border: "border-gold",       text: "text-gold-dark" },
  low:    { Icon: AlertTriangle,  cls: "bg-stone text-graphite",            border: "border-stone-warm", text: "text-graphite/70" },
  info:   { Icon: Activity,       cls: "bg-paars/30 text-paars-dark",       border: "border-paars",      text: "text-paars-dark" },
};

function RiskEventsBanner({ events }) {
  // Only HIGH + MEDIUM rows ever surface in the banner — INFO/LOW are
  // available on /risk-events history page (future) but would dilute the
  // banner's signal-to-noise.
  const surfaced = events.filter((e) => e.severity === "high" || e.severity === "medium");
  const top = surfaced.slice(0, 4);
  const high = surfaced.filter((e) => e.severity === "high").length;
  const med = surfaced.filter((e) => e.severity === "medium").length;
  if (top.length === 0) return null;
  return (
    <div data-testid="risk-banner" className="border border-burgundy/40 bg-burgundy/5 p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-burgundy" strokeWidth={2} />
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-burgundy font-bold">// CUT_RISK_WATCH</div>
            <h3 className="text-base font-bold text-graphite">
              {high > 0 && <span>{high} high-risk signal{high !== 1 ? "s" : ""}</span>}
              {high > 0 && med > 0 && <span className="text-graphite/45"> · </span>}
              {med > 0 && <span>{med} medium</span>}
              {high === 0 && med === 0 && <span>{events.length} new signal{events.length !== 1 ? "s" : ""}</span>}
              <span className="text-graphite/55"> on your watchlist</span>
            </h3>
          </div>
        </div>
        <Link to="/database/bdc" className="text-[10px] tracking-[0.2em] uppercase font-bold text-graphite/60 hover:text-paars-dark">
          Open Database →
        </Link>
      </div>
      <div className="space-y-2">
        {top.map((e) => {
          const m = SEV_META[e.severity] || SEV_META.info;
          return (
            <div
              key={e.event_id}
              data-testid={`risk-banner-row-${e.ticker}-${e.signal}`}
              className={`flex items-center gap-3 bg-champagne p-3 border-l-2 ${m.border}`}
            >
              <span className={`px-2 py-0.5 text-[9px] font-mono tracking-widest font-bold ${m.cls}`}>
                {e.severity.toUpperCase()}
              </span>
              <span className={`font-mono font-semibold ${m.text}`}>{e.ticker}</span>
              <span className="text-xs text-graphite/80 flex-1 truncate">{e.message.replace(`${e.ticker} `, "").replace(`${e.ticker}: `, "")}</span>
              <span className="text-[10px] font-mono text-graphite/45">{(e.fired_at || "").slice(0, 10)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
